import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface FilterOption {
  label: string;
  value: string;
}

interface FiltersState {
  hrAgentFilters: {
    experience: FilterOption | null;
    location: FilterOption[];
    degree: FilterOption[];
    previousCompany: FilterOption[];
  };
  translationFilters: {
    translation: "english" | "arabic";
    translationType: "general" | "legal";
  };
  procurementFilters: {
    procurementType: "sow_filler" | "reviewer" | "redraft" | "";
  };
  contractDashboardFilters: {
    groupCompany: string;
    contractType: string;
    dateRange: string;
  };
  templateReviewerFeedback: {
    accept_array: any[];
    reject_array: any[];
    modify_array: any[];
  };
  templateReviewerChecklistFeedback: {
    [messageId: string]: {
      accept_array: any[];
      reject_array: any[];
      modify_array: any[];
      add_new_array: any[];
    };
  };
  ndaValidationFeedback: {
    [messageId: string]: {
      accept_array: any[];
      reject_array: any[];
      modify_array: any[];
    };
  };
  outcomeModifierFeedback: {
    [messageId: string]: {
      accept_array: any[];
      modify_array: any[];
    };
  };
  consolidatorFeedback: {
    accept_array: any[];
    reject_array: any[];
    modify_array: any[];
    consolidated_issues: any[];
  };
  financeFeedback: {
    accept_array: any[];
    reject_array: any[];
    modify_array: any[];
    key_findings_modifications: any[];
    deviations_modifications: any[];
    gaps_modifications: any[];
  };
  legalFeedback: {
    accept_array: any[];
    reject_array: any[];
    modify_array: any[];
    key_findings_modifications: any[];
    deviations_modifications: any[];
    gaps_modifications: any[];
  };
  procurementFeedback: {
    accept_array: any[];
    reject_array: any[];
    modify_array: any[];
    key_findings_modifications: any[];
    deviations_modifications: any[];
    gaps_modifications: any[];
  };
  businessFeedback: {
    accept_array: any[];
    reject_array: any[];
    modify_array: any[];
    key_findings_modifications: any[];
    deviations_modifications: any[];
    gaps_modifications: any[];
  };
  sowAutoFillerFeedback: {
    [messageId: string]: {
      sow_modify_array: any[];
    };
  };
  humanCapitalFeedback: {
    modify_array: any[];
  };
}

const initialState: FiltersState = {
  hrAgentFilters: {
    experience: null,
    location: [],
    degree: [],
    previousCompany: [],
  },
  translationFilters: {
    translation: "arabic",
    translationType: "general",
  },
  procurementFilters: {
    procurementType: "",
  },
  contractDashboardFilters: {
    groupCompany: "",
    contractType: "",
    dateRange: "Oct 2024 - Dec 2024",
  },
  templateReviewerFeedback: {
    accept_array: [],
    reject_array: [],
    modify_array: [],
  },
  templateReviewerChecklistFeedback: {},
  ndaValidationFeedback: {},
  outcomeModifierFeedback: {},
  consolidatorFeedback: {
    accept_array: [],
    reject_array: [],
    modify_array: [],
    consolidated_issues: [],
  },
  financeFeedback: {
    accept_array: [],
    reject_array: [],
    modify_array: [],
    key_findings_modifications: [],
    deviations_modifications: [],
    gaps_modifications: [],
  },
  legalFeedback: {
    accept_array: [],
    reject_array: [],
    modify_array: [],
    key_findings_modifications: [],
    deviations_modifications: [],
    gaps_modifications: [],
  },
  procurementFeedback: {
    accept_array: [],
    reject_array: [],
    modify_array: [],
    key_findings_modifications: [],
    deviations_modifications: [],
    gaps_modifications: [],
  },
  businessFeedback: {
    accept_array: [],
    reject_array: [],
    modify_array: [],
    key_findings_modifications: [],
    deviations_modifications: [],
    gaps_modifications: [],
  },
  sowAutoFillerFeedback: {},
  humanCapitalFeedback: {
    modify_array: [],
  },
};

const filtersSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    setHrAgentFilters(
      state,
      action: PayloadAction<FiltersState["hrAgentFilters"]>
    ) {
      state.hrAgentFilters = action.payload;
    },
    resetHrAgentFilters(state) {
      state.hrAgentFilters = initialState.hrAgentFilters;
    },
    setTranslationFilters(
      state,
      action: PayloadAction<Partial<FiltersState["translationFilters"]>>
    ) {
      state.translationFilters = {
        ...state.translationFilters,
        ...action.payload,
      };
    },
    resetTranslationFilters(state) {
      state.translationFilters = initialState.translationFilters;
    },
    setProcurementFilters(
      state,
      action: PayloadAction<Partial<FiltersState["procurementFilters"]>>
    ) {
      state.procurementFilters = {
        ...state.procurementFilters,
        ...action.payload,
      };
    },
    resetProcurementFilters(state) {
      state.procurementFilters = initialState.procurementFilters;
    },
    setContractDashboardFilters(
      state,
      action: PayloadAction<Partial<FiltersState["contractDashboardFilters"]>>
    ) {
      state.contractDashboardFilters = {
        ...state.contractDashboardFilters,
        ...action.payload,
      };
    },
    resetContractDashboardFilters(state) {
      state.contractDashboardFilters = initialState.contractDashboardFilters;
    },
    setTemplateReviewerFeedback(
      state,
      action: PayloadAction<Partial<FiltersState["templateReviewerFeedback"]>>
    ) {
      state.templateReviewerFeedback = {
        ...state.templateReviewerFeedback,
        ...action.payload,
      };
    },
    resetTemplateReviewerFeedback(state) {
      state.templateReviewerFeedback = initialState.templateReviewerFeedback;
    },
    setTemplateReviewerChecklistFeedback(
      state,
      action: PayloadAction<{
        messageId: string;
        feedback: {
          accept_array: any[];
          reject_array: any[];
          modify_array: any[];
          add_new_array: any[];
        };
      }>
    ) {
      const { messageId, feedback } = action.payload;
      if (!state.templateReviewerChecklistFeedback[messageId]) {
        state.templateReviewerChecklistFeedback[messageId] = {
          accept_array: [],
          reject_array: [],
          modify_array: [],
          add_new_array: [],
        };
      }
      state.templateReviewerChecklistFeedback[messageId] = {
        ...state.templateReviewerChecklistFeedback[messageId],
        ...feedback,
      };
    },
    resetTemplateReviewerChecklistFeedback(state, action: PayloadAction<string>) {
      const messageId = action.payload;
      if (state.templateReviewerChecklistFeedback[messageId]) {
        delete state.templateReviewerChecklistFeedback[messageId];
      }
    },
    setNdaValidationFeedback(
      state,
      action: PayloadAction<{
        messageId: string;
        feedback: {
          accept_array: any[];
          reject_array: any[];
          modify_array: any[];
        };
      }>
    ) {
      const { messageId, feedback } = action.payload;
      if (!state.ndaValidationFeedback[messageId]) {
        state.ndaValidationFeedback[messageId] = {
          accept_array: [],
          reject_array: [],
          modify_array: [],
        };
      }
      state.ndaValidationFeedback[messageId] = {
        ...state.ndaValidationFeedback[messageId],
        ...feedback,
      };
    },
    resetNdaValidationFeedback(state, action: PayloadAction<string>) {
      const messageId = action.payload;
      if (state.ndaValidationFeedback[messageId]) {
        delete state.ndaValidationFeedback[messageId];
      }
    },
    setOutcomeModifierFeedback(
      state,
      action: PayloadAction<{
        messageId: string;
        feedback: {
          accept_array: any[];
          modify_array: any[];
        };
      }>
    ) {
      const { messageId, feedback } = action.payload;
      if (!state.outcomeModifierFeedback[messageId]) {
        state.outcomeModifierFeedback[messageId] = {
          accept_array: [],
          modify_array: [],
        };
      }
      state.outcomeModifierFeedback[messageId] = {
        ...state.outcomeModifierFeedback[messageId],
        ...feedback,
      };
    },
    resetOutcomeModifierFeedback(state, action: PayloadAction<string>) {
      const messageId = action.payload;
      if (state.outcomeModifierFeedback[messageId]) {
        delete state.outcomeModifierFeedback[messageId];
      }
    },
    setConsolidatorFeedback(
      state,
      action: PayloadAction<Partial<FiltersState["consolidatorFeedback"]>>
    ) {
      state.consolidatorFeedback = {
        ...state.consolidatorFeedback,
        ...action.payload,
      };
    },
    resetConsolidatorFeedback(state) {
      state.consolidatorFeedback = initialState.consolidatorFeedback;
    },
    setFinanceFeedback(
      state,
      action: PayloadAction<Partial<FiltersState["financeFeedback"]>>
    ) {
      state.financeFeedback = { ...state.financeFeedback, ...action.payload };
    },
    resetFinanceFeedback(state) {
      state.financeFeedback = initialState.financeFeedback;
    },
    setLegalFeedback(
      state,
      action: PayloadAction<Partial<FiltersState["legalFeedback"]>>
    ) {
      state.legalFeedback = { ...state.legalFeedback, ...action.payload };
    },
    resetLegalFeedback(state) {
      state.legalFeedback = initialState.legalFeedback;
    },
    setProcurementFeedback(
      state,
      action: PayloadAction<Partial<FiltersState["procurementFeedback"]>>
    ) {
      state.procurementFeedback = {
        ...state.procurementFeedback,
        ...action.payload,
      };
    },
    resetProcurementFeedback(state) {
      state.procurementFeedback = initialState.procurementFeedback;
    },
    setBusinessFeedback(
      state,
      action: PayloadAction<Partial<FiltersState["businessFeedback"]>>
    ) {
      state.businessFeedback = { ...state.businessFeedback, ...action.payload };
    },
    resetBusinessFeedback(state) {
      state.businessFeedback = initialState.businessFeedback;
    },
    setSowAutoFillerFeedback(
      state,
      action: PayloadAction<{
        messageId: string;
        feedback: {
          sow_modify_array: any[];
        };
      }>
    ) {
      const { messageId, feedback } = action.payload;
      if (!state.sowAutoFillerFeedback[messageId]) {
        state.sowAutoFillerFeedback[messageId] = {
          sow_modify_array: [],
        };
      }
      state.sowAutoFillerFeedback[messageId] = {
        ...state.sowAutoFillerFeedback[messageId],
        ...feedback,
      };
    },
    resetSowAutoFillerFeedback(state, action: PayloadAction<string>) {
      const messageId = action.payload;
      delete state.sowAutoFillerFeedback[messageId];
    },
    setHumanCapitalFeedback(
      state,
      action: PayloadAction<Partial<FiltersState["humanCapitalFeedback"]>>
    ) {
      state.humanCapitalFeedback = {
        ...state.humanCapitalFeedback,
        ...action.payload,
      };
    },
    resetHumanCapitalFeedback(state) {
      state.humanCapitalFeedback = initialState.humanCapitalFeedback;
    },
  },
});

export const {
  setHrAgentFilters,
  resetHrAgentFilters,
  setTranslationFilters,
  resetTranslationFilters,
  setProcurementFilters,
  resetProcurementFilters,
  setContractDashboardFilters,
  resetContractDashboardFilters,
  setTemplateReviewerFeedback,
  resetTemplateReviewerFeedback,
  setTemplateReviewerChecklistFeedback,
  resetTemplateReviewerChecklistFeedback,
  setNdaValidationFeedback,
  resetNdaValidationFeedback,
  setOutcomeModifierFeedback,
  resetOutcomeModifierFeedback,
  setConsolidatorFeedback,
  resetConsolidatorFeedback,
  setFinanceFeedback,
  resetFinanceFeedback,
  setLegalFeedback,
  resetLegalFeedback,
  setProcurementFeedback,
  resetProcurementFeedback,
  setBusinessFeedback,
  resetBusinessFeedback,
  setSowAutoFillerFeedback,
  resetSowAutoFillerFeedback,
  setHumanCapitalFeedback,
  resetHumanCapitalFeedback,
} = filtersSlice.actions;
export default filtersSlice.reducer;
